import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/base/Seo';
import MovieCard from '@/components/feature/MovieCard';
import PlayerPanel from '@/pages/detail/components/PlayerPanel';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';

const typeLabel: Record<string, string> = { movie: '电影', tv: '电视剧', variety: '综艺' };

export default function DetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center text-center px-4 py-20">
          <i className="ri-film-line text-5xl text-background-300 mb-4"></i>
          <p className="text-foreground-700">抱歉，没有找到该影视作品的资料</p>
          <button
            onClick={() => navigate('/')}
            className="mt-5 px-5 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-sm cursor-pointer whitespace-nowrap"
          >
            返回首页
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const related = getRelatedMovies(movie, 4);
  const favorite = isFavorite(movie.id);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Seo
        title={`${movie.title} - 详情介绍 - 菠萝蜜网`}
        description={movie.description.slice(0, 120)}
        keywords={`${movie.title},${movie.genres.join(',')},菠萝蜜网`}
      />
      <Navbar />
      <main className="flex-1">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <nav className="text-xs text-foreground-500 flex items-center gap-1.5 mb-5 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/category/${movie.type}`} className="hover:text-primary-600 cursor-pointer">{typeLabel[movie.type]}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800 truncate">{movie.title}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            <div className="w-full lg:w-80 shrink-0">
              <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img src={movie.cover} alt={`${movie.title} 封面`} title={movie.title} className="w-full h-full object-cover object-top" />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={() => toggleFavorite(movie.id)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                    favorite ? 'bg-accent-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={favorite ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorite ? '已收藏' : '收藏'}
                </button>
                <div className="relative">
                  <button
                    onClick={() => setShareOpen((v) => !v)}
                    aria-label="分享"
                    className="w-11 h-11 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-700 cursor-pointer"
                  >
                    <i className="ri-share-forward-line"></i>
                  </button>
                  {shareOpen && (
                    <div className="absolute right-0 top-12 bg-background-50 shadow-lg border border-background-200 rounded-lg p-3 flex gap-3 z-10">
                      <i className="ri-wechat-fill text-xl text-primary-500 cursor-pointer"></i>
                      <i className="ri-weibo-fill text-xl text-accent-500 cursor-pointer"></i>
                      <i className="ri-link text-xl text-foreground-500 cursor-pointer"></i>
                    </div>
                  )}
                </div>
              </div>

              <Link
                to={`/category/${movie.type}`}
                className="mt-4 flex items-center justify-between bg-background-100 hover:bg-background-200 rounded-lg px-4 py-3 text-sm text-foreground-700 cursor-pointer"
              >
                所属分类：{typeLabel[movie.type]}
                <i className="ri-arrow-right-s-line"></i>
              </Link>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900">{movie.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-500"></i>{movie.rating.toFixed(1)} 分</span>
                <span>{movie.year}</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <span key={g} className="px-2.5 py-1 bg-primary-100 text-primary-700 text-xs rounded-full">{g}</span>
                ))}
              </div>

              <div className="mt-6 bg-background-100 rounded-lg p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-foreground-500">导演：</span><span className="text-foreground-800">{movie.director}</span></div>
                <div><span className="text-foreground-500">主演：</span><span className="text-foreground-800">{movie.actors.join('、')}</span></div>
                <div><span className="text-foreground-500">地区：</span><span className="text-foreground-800">{movie.region}</span></div>
                <div><span className="text-foreground-500">片长 / 集数：</span><span className="text-foreground-800">{movie.duration}</span></div>
                {movie.updatedEpisode && (
                  <div><span className="text-foreground-500">更新状态：</span><span className="text-foreground-800">{movie.updatedEpisode}</span></div>
                )}
              </div>

              <div className="mt-6">
                <h2 className="text-base font-bold text-foreground-900 mb-2">内容简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{movie.description}</p>
              </div>

              <div className="mt-6">
                <h2 className="text-base font-bold text-foreground-900 mb-3">播放示意</h2>
                <PlayerPanel cover={movie.banner} title={movie.title} />
              </div>
            </div>
          </div>

          {related.length > 0 && (
            <section className="mt-12">
              <h2 className="text-lg md:text-xl font-bold text-foreground-900 mb-4">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} showType />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}